/**
 * Google Enhanced Ecommerce Object
 *
 * Core business logic and functionality for Google Enhanced Ecommerce.
 *
 * https://confluence.int.plus.net/display/DIGITAL/Google+Enhanced+Ecommerce
 */
const core = {

    /**
     * initialise - calls functions to trigger ecommerce functionality.
     *
     * @param  {array}  modules  Array of function names and parameters you wish to call.
     * @param {bool} performStorageCheck Whether to check storage capabilities
     * @param {function} callback Callback on successful initialisation
     *
     * ---------------------
     * Possible array values
     * ---------------------
     * - String: the name of a function to call without any parameters.
     * - Object: a key/value pair, key is a function name and value is an array of parameters.
     *
     * ---------------------
     * Example usage
     * ---------------------
     * Call "impressions" function with no params, and "checkout" with "visa" as a param:
     * gaEcommerce.initialise(["impressions", {"checkout": ["visa"]}]);
     *
     * This is the the same as:
     * gaEcommerce.impressions.initialise();
     * gaEcommerce.checkout.initialise("visa");
     * ---------------------
     *
     * @returns  {void}
     */
    initialise: (modules, performStorageCheck, callback) => {

        if (gaEcommerce.core.isInitialised) return;

        // Overload to allow the 2nd param to be the callback
        if (typeof performStorageCheck === "function") {
            callback = performStorageCheck;
            performStorageCheck = true;
        }

        if (typeof performStorageCheck === "undefined") {
            performStorageCheck = true;
        }

        if (performStorageCheck && (
            !gaEcommerce.core.checkStorageCapabilities()
            || !gaEcommerce.core.initialiseStorage())
        ) {
            return;
        }

        // Get the client time on page load
        gaEcommerce.clientTimeOnLoad = Math.floor(Date.now() / 1000);

        if (typeof modules === 'object') {
            modules.map((moduleInfo) => {
                // If it's a string, we have a module name
                if (typeof moduleInfo === 'string') {
                    gaEcommerce[moduleInfo].initialise();
                } else if (typeof moduleInfo === 'object') {
                    // Key is used to represent the module name
                    // e.g. 'checkout' is the key here: {'checkout': ['visa']}
                    const moduleName = Object.keys(moduleInfo)[0];
                    let params = moduleInfo[moduleName]; // Value is an array of parameters

                    if (!Array.isArray(params)) {
                        params = [params];
                    }

                    gaEcommerce[moduleName].initialise.apply(null, params);
                }
            });
        }

        gaEcommerce.purchase.resetPurchaseEvents();

        if (typeof callback === 'function') {
            callback();
        }

        gaEcommerce.core.isInitialised = true;
    },

    /*
     * Check storage capabilities
     *
     * @return {boolean}
     */
    checkStorageCapabilities: () => {
        if (typeof clientData === 'undefined') {
            console.warn('[ga-ecommerce.js] Failed to load clientData dependency');
            return false;
        }

        return true;
    },

    /*
     * Initialise any client side storage variables
     *
     * @return {boolean}
     */
    initialiseStorage: () => {
        if (!sessionStorage['ecomProducts']) {
            clientData.storage.setData({name: 'ecomProducts', value: JSON.stringify([])}, 'sessionStorage');
        }

        if (!sessionStorage['ecomProducts']) {
            console.warn('[ga-ecommerce.js] Failed to initialise storage data using clientData dependency');
            return false;
        }

        return true;
    },

    /**
     * sendData - send an event to the dataLayer.
     *
     * @param {object} ecomObject  The object to be sent to GA. (which is put
     * into the ecommerce property)
     * @param {string} event  An optional descriptor of the event (e.g. "productClick").
     *
     * @returns {boolean} true/false depending on whether data is sent successfully
     */
    sendData: (ecomObject, event) => {

        const ecomData = {ecommerce: ecomObject};

        if (typeof event !== "undefined"){
            ecomData.event = event;
        }

        try {
            window.dataLayer.push(ecomData);
        } catch (e) {
            return false;
        }

        return true;
    },

    /**
     * Sends the add data to GA
     *
     * @param {array} addedProducts
     *
     * @returns {void}
     */
    sendAddData: (addedProducts) => {
        if (addedProducts.length > 0) {
            gaEcommerce.core.sendData({
                add: {
                    products: addedProducts
                }
            }, 'addToCart');
        }
    },

    /**
     * Send the GA Remove data
     *
     * @param {array} removedProducts
     * @returns {void}
     */
    sendRemoveData: (removedProducts) => {
        if (removedProducts.length > 0) {
            gaEcommerce.core.sendData({
                remove: {
                    products: removedProducts
                }
            }, 'removeFromCart');
        }
    },

    /**
     * Decide the current affiliate from the hostname
     *
     * @returns {string}
     */
    getAffiliate: () => {
        const host = window.location.hostname,
              trim = host.substring(host.indexOf('www.') + 4, host.lastIndexOf('.'));

        switch (trim) {
            case 'johnlewisbroadband':
                return 'John Lewis';
            default:
                return 'Plusnet';

        }
    },

    /**
     * validateProduct - Check product has required values
     *
     * @param  {object}  productData  Raw product data from data-ecom-product attributes
     * @returns  {object}  A fully formed product made up of properties in key/value pairs
     */
    validateProduct: (productData) => {

        // Get a copy of the object rather than a reference,
        // otherwise we'll get left over data from other calls to this function.
        productData = gaEcommerce.util.parseJsonObj(JSON.stringify(productData));

        if (Object.keys(productData).length) {
            // If id/name not set then add a flag to show an error has occured
            productData.id = gaEcommerce.util.dataExists(productData, 'id')
                ? productData.id
                : 'undefined';
            productData.name =gaEcommerce.util.dataExists(productData, 'name')
                ? productData.name
                : 'undefined';

            return productData;
        }

        return false;
    },

    /**
     * includeDynamicProductProperties - Include additional product properties which are generated client side
     *
     * @param  {object}  productElem  DOM element
     * @param  {object}  productData  Key value pairs of product data
     * @param  {object}  propertiesToInclude  Key value pairs defining which product properties to include/exclude
     * e.g. {"list": true, "position": false}, if a property is omitted it will default to true.
     *
     * @returns  {object}  A fully formed product with the additional properties included
     */
    includeDynamicProductProperties: (productElem, productData, propertiesToInclude = {}) => {

        if (!(productElem instanceof Element)) {
            return productData;
        }

        const propertiesList = [
            'list',
            'position',
            'timeUntilPromoExpires',
            'quantity',
            'dimension48'
        ];

        propertiesList.map(function (propertyName) {
            propertiesToInclude[propertyName] = propertiesToInclude[propertyName] || true;
        });

        const productListElem = productElem.closest('[data-ecom-list]'),
              offerExpiry  = productElem.dataset.offerExpiry;

        if (propertiesToInclude.list && productListElem) {
            productData.list = productListElem.dataset.ecomList;
        }

        if (propertiesToInclude.position) {
            productData.position = productElem.dataset.ecomPosition
                || productElem.dataset.interactionModulePosition
                || 0;
        }

        if (propertiesToInclude.timeUntilPromoExpires) {
            productData = gaEcommerce.util.includeTimeUntilPromoExpires(offerExpiry, productData);
        }

        if (propertiesToInclude.quantity) {
            productData.quantity = 1;
        }

        if (propertiesToInclude['dimension48']) {
            productData['dimension48'] = gaEcommerce.core.getJourney();
        }

        return productData;
    },

    /**
     * Lazy load product elements when we need them, and return an object indexed by the product.id
     *
     * @returns {object}
     */
    getProductElements: function () {
        if (typeof gaEcommerce.core.productElements === 'undefined') {
            gaEcommerce.core.productElements = {};

            document.querySelectorAll('[data-ecom-product]').forEach((el) => {
                const productInfo = gaEcommerce.util.parseJsonObj(el.dataset.ecomProduct);
                gaEcommerce.core.productElements[productInfo.id] = el;
            });
        }

        return gaEcommerce.core.productElements;
    },

    /**
     * Get a product element by its product id
     *
     * @param {string} id
     * @returns {null|object}
     */
    getProductElementById: (id) => {
        const products = gaEcommerce.core.getProductElements();

        return products[id]
            ? products[id]
            : null;
    },

    /**
     * Check if element is a bundleable call feature
     *
     * @param {string} id
     *
     * @returns {boolean}
     */
    isProductBundleable: (id) => {
        const features = gaEcommerce.core.getBundleableCallFeatures();
        return (typeof features[id] !== 'undefined');
    },

    /**
     * Lazy load and get all bundleable call features
     *
     * @returns {object}
     */
    getBundleableCallFeatures: function () {
        if (typeof gaEcommerce.core.callFeatures === 'undefined') {
            gaEcommerce.core.callFeatures = {};
            const elements = document.querySelectorAll('#callFeaturesExpander [data-ecom-product]');

            elements.forEach((element) => {
                const product = gaEcommerce.util.parseJsonObj(element.dataset.ecomProduct);
                gaEcommerce.core.callFeatures[product.id] = element;
            });
        }

        return gaEcommerce.core.callFeatures;
    },

    /**
     * Update element properties
     *
     * @param {object} ecomElement DOM Element
     * @param {object} propertiesToModify Properties to update on the element
     *
     * @returns {void}
     */
    changeDataEcomProductAttribute: (ecomElement, propertiesToModify) => {   
        ecomElement.dataset.ecomProduct = JSON.stringify(
            gaEcommerce.util.mergeObject(
                gaEcommerce.util.parseJsonObj(ecomElement.dataset.ecomProduct), propertiesToModify
            )
        );
    },

    reduceElementsFromProduct: (list, product) => {
        const productElement = gaEcommerce.core.getProductElementById(product.id);

        if (productElement !== null) {
            list.push({ element: productElement, dataOverride: product });
        }

        return list;
    },

    /**
     * Get the name of the current journey
     * This can be set by including a data-ecom-journey
     * attribute on the body or wrapper of a page.
     *
     * Defaults to: broadband|sign-up|residential
     *
     * @returns {string}
     */
    getJourney: () => {
        if (!gaEcommerce.core.journey) {
            const journeyContainer = document.querySelector('[data-ecom-journey]');
            gaEcommerce.core.journey = journeyContainer?.dataset?.ecomJourney
                ? journeyContainer.dataset.ecomJourney
                : 'broadband|sign-up|residential'
        }
        return gaEcommerce.core.journey;
    }
};

export default core;
